import { useState, useEffect } from 'react';
import { Header } from '../components/layout/Header';

const API_BASE = '/api';

interface Pipeline {
  id: string;
  name: string;
  description: string;
  source: string;
  sink: string;
  status: string;
  parallelism: number;
  checkpointing_interval_ms: number;
  events_per_second: number;
  processing_latency_ms: number;
  backpressure: boolean;
  uptime_hours: number;
  created_date: string;
}

interface Statistics {
  total_pipelines: number;
  running_pipelines: number;
  stopped_pipelines: number;
  total_operators: number;
  healthy_operators: number;
  total_events_per_second: number;
  avg_latency_ms: number;
  pipelines_with_backpressure: number;
  total_parallelism: number;
  avg_cpu_usage: number;
}

export default function StreamProcessingPage() {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pipelinesRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/streaming/pipelines`),
        fetch(`${API_BASE}/streaming/statistics`)
      ]);
      setPipelines(await pipelinesRes.json());
      setStatistics(await statsRes.json());
    } catch (error) {
      console.error('Error fetching stream processing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'bg-green-100 text-green-800';
      case 'stopped': return 'bg-gray-100 text-gray-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Stream Processing</h1>
          <p className="mt-2 text-gray-600">Monitor real-time data pipelines and streaming analytics</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Pipelines</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_pipelines}</p>
              <p className="text-sm text-green-600">{statistics.running_pipelines} running</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Events/Second</p>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(statistics.total_events_per_second)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Latency</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.avg_latency_ms}ms</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Parallelism</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_parallelism}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Backpressure</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.pipelines_with_backpressure}</p>
            </div>
          </div>
        )}

        {/* Pipelines Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Streaming Pipelines ({pipelines.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pipeline</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source/Sink</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Throughput</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Latency</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parallelism</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uptime</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pipelines.map((pipeline) => (
                  <tr key={pipeline.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{pipeline.name}</div>
                      <div className="text-sm text-gray-500">{pipeline.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <span className="text-blue-600">{pipeline.source}</span>
                        <span className="text-gray-400 mx-2">→</span>
                        <span className="text-purple-600">{pipeline.sink}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(pipeline.status)}`}>
                        {pipeline.status}
                      </span>
                      {pipeline.backpressure && (
                        <span className="ml-2 px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                          backpressure
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(pipeline.events_per_second)}/s
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {pipeline.processing_latency_ms}ms
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {pipeline.parallelism}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {pipeline.uptime_hours}h
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
